/**
 * Parse a Google Drive / Docs URL into a Drive fileId.
 * Mirrors scripts/utils/google-drive-url.js (palette + import picker).
 */

const DRIVE_FILE_ID_PATTERNS = [
  /\/document\/d\/([a-zA-Z0-9_-]+)/,
  /\/file\/d\/([a-zA-Z0-9_-]+)/,
  /\/spreadsheets\/d\/([a-zA-Z0-9_-]+)/,
  /\/presentation\/d\/([a-zA-Z0-9_-]+)/,
  /[?&]id=([a-zA-Z0-9_-]+)/,
]

function looksLikeGoogleDriveHost(input: string): boolean {
  const trimmed = input.trim().toLowerCase()
  return (
    trimmed.includes('docs.google.com') ||
    trimmed.includes('drive.google.com') ||
    trimmed.includes('drive.googleusercontent.com')
  )
}

/** Parse a Drive/Docs URL or (backend-only) bare file ID into a fileId. */
export function parseDriveFileId(input: string): string | null {
  const trimmed = String(input || '').trim()
  if (!trimmed) return null

  if (/^[a-zA-Z0-9_-]{10,}$/.test(trimmed) && !trimmed.includes('/')) {
    return trimmed
  }

  for (const re of DRIVE_FILE_ID_PATTERNS) {
    const match = trimmed.match(re)
    if (match?.[1]) return match[1]
  }

  return null
}

export type GoogleDriveUrlInfo = {
  fileId: string
  url: string
}

/**
 * Command palette only: require a Google host so bare IDs do not collide
 * with Maater UUIDs or other paste.
 */
export function extractGoogleDriveUrlInfo(input: string): GoogleDriveUrlInfo | null {
  const url = input.trim()
  if (!url) return null
  const isHttp = url.startsWith('http://') || url.startsWith('https://')
  if (!isHttp || !looksLikeGoogleDriveHost(url)) return null
  const fileId = parseDriveFileId(url)
  if (!fileId) return null
  return { fileId, url }
}

export function isGoogleDriveUrlQuery(input: string): boolean {
  return extractGoogleDriveUrlInfo(input) !== null
}
